import { Box, Flex, Text, Divider, HStack, VStack, Button, Modal, ModalFooter, ModalContent, ModalBody, ModalHeader, ModalCloseButton, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import { useState } from "react";

import { G1Button } from "../G1Button";
import { formatAmount } from "./helpers";
import { Card, ProgressBar } from "./UI";

interface UsersCardProps {
    activeUsers: number;
    maxUsers: number;
    reduceUsers: number;
    pricePerExtraUser: number;
    prorataPricePerUser: number;
    hasPendingOrder: boolean;
    isCancelationRequested: boolean;
    isTrialPeriod?: boolean;
    requesterNameToReduce?: string;
    onAddUsers: (count: number) => void;
    onCancelReduceRequest: () => void;
    onReduceUsers: (count: number) => void;
}

export function CollaboratorsCard(props: UsersCardProps) {
    const { activeUsers, maxUsers, hasPendingOrder, isCancelationRequested, isTrialPeriod, pricePerExtraUser, prorataPricePerUser, reduceUsers, requesterNameToReduce, onAddUsers, onCancelReduceRequest, onReduceUsers } = props;
    const [usersToAdd, setUsersToAdd]       = useState<number>(0);
    const [usersToRemove, setUsersToRemove] = useState<number>(0);
    const addModal    = useDisclosure();
    const reduceModal = useDisclosure();

    const extraUsers      = Math.max(0, activeUsers - 1);
    const canAddUsers     = !isTrialPeriod && !isCancelationRequested && activeUsers <= maxUsers;
    const canRemoveUsers  = !isTrialPeriod && !isCancelationRequested && activeUsers > 1;

    function handleAddUsers(): void {
        onAddUsers(usersToAdd);
        addModal.onClose();
    }

    function handleReduceUsers(): void {
        onReduceUsers(usersToRemove);
        reduceModal.onClose();
    }

    function openAddModal(): void {
        setUsersToAdd(0);
        addModal.onOpen();
    } 

    function openRemoveModal(): void {
        setUsersToRemove(0);
        reduceModal.onOpen();
    }

    return (
        <>
            <Card>
                <Flex justify="space-between" align="flex-start" flexWrap="wrap" mb={5} gap={3}>
                    <Text fontFamily="heading" fontSize="lg" color="gray.900">Collaborateurs</Text>
                    <HStack spacing={2}>
                        {canRemoveUsers && (
                            <Button size="xs" variant="ghost" color="red.400" _hover={{ bg: "red.50" }} isDisabled={hasPendingOrder || reduceUsers !== 0} onClick={openRemoveModal}>
                                − Retirer
                            </Button>
                        )}
                        {canAddUsers && (
                            <Button size="xs" variant="outline" borderColor="brand.500" color="brand.500" _hover={{ bg: "brand.50" }} isDisabled={hasPendingOrder || reduceUsers !== 0} onClick={openAddModal}>
                                + Ajouter
                            </Button>
                        )}
                    </HStack>
                </Flex>

                <VStack spacing={4} align="stretch">
                    <Box>
                        <Flex justify="end" mb={1}>
                            <Text fontSize="sm" fontWeight="700" color="gray.800">
                                {activeUsers}
                                <Text as="span" fontSize="xs" color="gray.400" fontWeight="400"> / {maxUsers} max</Text>
                            </Text>
                        </Flex>
                        <ProgressBar value={activeUsers} max={maxUsers} color="#3354f4" />
                    </Box>

                    <Box bg="surface.subtle" borderRadius="12px" p={4}>
                        <VStack spacing={2} align="stretch">
                            <Flex justify="space-between">
                                <Text fontSize="xs" color="gray.500">Inclus dans le plan</Text>
                                <Text fontSize="xs" fontWeight="600" color="gray.700">1</Text>
                            </Flex>
                            {extraUsers > 0 && (
                                <Flex justify="space-between">
                                    <Text fontSize="xs" color="gray.500">Supplémentaires</Text>
                                    <Text fontSize="xs" fontWeight="600" color="gray.700">{extraUsers}</Text>
                                </Flex>
                            )}
                            <Divider borderColor="gray.200" />
                            <Flex justify="space-between" align="center">
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wider">Actifs</Text>
                                <HStack spacing={2}>
                                    <Box w="7px" h="7px" borderRadius="full" bg="green.400" />
                                    <Text fontSize="lg" fontWeight="700" color="gray.900">{activeUsers}</Text>
                                </HStack>
                            </Flex>
                        </VStack>
                    </Box>

                    {reduceUsers > 0 && (
                        <>
                            <Box bg="orange.50" borderRadius="10px" p={3} border="1px solid" borderColor="orange.100">
                                <VStack spacing={1} align="start">
                                    <Flex justify="space-between" align="flex-start" alignItems="center" flexWrap="wrap" mb={2} gap={3}>
                                        <Text fontSize="xs" fontWeight="600" color="orange.700">
                                            Suppression de {reduceUsers} collaborateur{reduceUsers > 0 ? 's' : ''}
                                        </Text>
                                        <Button size="xs" variant="outline" borderColor="orange.700" color="orange.700" _hover={{ bg: "brand.50" }}
                                                onClick={onCancelReduceRequest}>
                                            Annuler la demande
                                        </Button>
                                    </Flex>
                                    <Text fontSize="xs" color="orange.600" lineHeight="1.6">
                                        La demande de réduction du nombre de collaborateurs effectuée par {requesterNameToReduce} sera{" "}
                                        prise en compte au <Text as="span" fontWeight="700">renouvellement</Text> de votre abonnement.
                                        Aucun remboursement ne sera effectué pour la période en cours.
                                    </Text>
                                </VStack>
                            </Box>
                            <Box bg="red.50" borderRadius="12px" p={4} border="1px solid" borderColor="red.100">
                                <Text fontSize="sm" fontWeight="600" color="red.700" mb={1}>
                                    Action requise de votre part
                                </Text>
                                <Text fontSize="xs" color="red.600" lineHeight="1.6">
                                    Pensez à{" "}
                                    <Text as="span" fontWeight="700">supprimer les comptes utilisateurs</Text>{" "}
                                    correspondants dans votre espace. Sans cette action, des utilisateurs actifs
                                    pourraient se retrouver sans accès lors du renouvellement, sans avertissement préalable.
                                </Text>
                            </Box>
                        </>
                    )}

                    <Text fontSize="xs" color="gray.400" textAlign="center">
                        Utilisateur supplémentaire : {formatAmount(pricePerExtraUser)} HT / an
                    </Text>
                </VStack>
            </Card>

            <Modal isOpen={addModal.isOpen} onClose={addModal.onClose} size="sm" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading">Ajouter des collaborateurs</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Text fontSize="sm" fontWeight="500" color="gray.700" mb={3}>Combien souhaitez-vous en ajouter ?</Text>
                                <Flex align="center" gap={4} justify="center">
                                    <Button size="sm" variant="outline" borderColor="gray.200" isDisabled={usersToAdd <= 0}
                                            onClick={() => setUsersToAdd((n) => Math.max(0, n - 1))}>
                                        −
                                    </Button>
                                    <Text fontWeight="700" fontSize="2xl" color="gray.900" minW="40px" textAlign="center">{usersToAdd}</Text>
                                    <Button size="sm" variant="outline" borderColor="gray.200"
                                            onClick={() => setUsersToAdd((n) => n + 1)}>
                                        +
                                    </Button>
                                </Flex>
                            </Box>

                            <Box borderRadius="12px" border="1px solid" p={4} borderColor="brand.100" bg="brand.50">
                                <Flex justify="space-between" align="flex-start" mb={1}>
                                    <Text fontSize="sm" fontWeight="600" color="brand.700">Facturation immédiate</Text>
                                    <Text fontSize="sm" fontWeight="800" color="brand.700">{formatAmount(prorataPricePerUser * usersToAdd)}</Text>
                                </Flex>
                                <Text fontSize="xs" color="brand.500" lineHeight="1.6">
                                    {`Au prorata jusqu'à la date de renouvellement, puis ${formatAmount(pricePerExtraUser)} HT / collaborateur / an`}
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={addModal.onClose} />
                        <G1Button component="form" variant="important" title="Confirmer" flex={1} isDisabled={usersToAdd === 0} onClick={handleAddUsers} />
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={reduceModal.isOpen} onClose={reduceModal.onClose} size="sm" isCentered>
                <ModalOverlay backdropFilter="blur(6px)" />
                <ModalContent borderRadius="20px" p={2}>
                    <ModalHeader fontFamily="heading" color="gray.800">Retirer des collaborateurs</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <Box>
                                <Text fontSize="sm" fontWeight="500" color="gray.700" mb={3}>
                                    Combien souhaitez-vous en retirer ?
                                </Text>
                                <Flex align="center" gap={4} justify="center">
                                    <Button size="sm" variant="outline" borderColor="gray.200" isDisabled={usersToRemove <= 0}
                                            onClick={() => setUsersToRemove((n) => Math.max(0, n - 1))}>
                                        −
                                    </Button>
                                    <Text fontWeight="700" fontSize="2xl" color="gray.900" minW="40px" textAlign="center">{usersToRemove}</Text>
                                    <Button size="sm" variant="outline" borderColor="gray.200" 
                                            onClick={() => setUsersToRemove((n) => Math.min(maxUsers-1, n + 1))}>
                                        +
                                    </Button>
                                </Flex>
                            </Box>

                            <Box bg="orange.50" borderRadius="12px" p={4} border="1px solid" borderColor="orange.100">
                                <Text fontSize="sm" fontWeight="600" color="orange.700" mb={1}>
                                    Effectif au renouvellement
                                </Text>
                                <Text fontSize="xs" color="orange.600" lineHeight="1.6">
                                    Cette modification ne prend pas effet immédiatement.
                                    Vous continuerez à avoir accès aux {maxUsers} collaborateurs actuels jusqu'à la prochaine
                                    date de renouvellement.{" "}
                                    <Text as="span" fontWeight="700">Aucun remboursement</Text> ne sera effectué.
                                </Text>
                            </Box>

                            <Box bg="red.50" borderRadius="12px" p={4} border="1px solid" borderColor="red.100">
                                <Text fontSize="sm" fontWeight="600" color="red.700" mb={1}>
                                    Action requise de votre part
                                </Text>
                                <Text fontSize="xs" color="red.600" lineHeight="1.6">
                                    Pensez à{" "}
                                    <Text as="span" fontWeight="700">supprimer les comptes utilisateurs</Text>{" "}
                                    correspondants dans votre espace. Sans cette action, des utilisateurs actifs
                                    pourraient se retrouver sans accès lors du renouvellement, sans avertissement préalable.
                                </Text>
                            </Box>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={2}>
                        <G1Button component="form" variant="cancel" title="Annuler" flex={1} onClick={reduceModal.onClose} />
                        <G1Button component="form" variant="important" title="Confirmer" flex={1} isDisabled={usersToRemove === 0} onClick={handleReduceUsers} />
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
}